import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/lib/api";

const toSafeNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

// 2026-07-16 — Max holding geri sayım rozeti (saf UI/görünürlük).
// Eşik (%) admin panelden gelir (max_holding_badge_threshold_pct); 0/None = rozet yok.
// Kalan süre, limitin eşik yüzdesinin altına düşünce metin döner.
const maxHoldingCountdown = (row) => {
  const limitHours = toSafeNumber(row?.max_holding_hours_snapshot, 0);
  const thresholdPct = toSafeNumber(row?.max_holding_badge_threshold_pct, 0);
  if (limitHours <= 0 || thresholdPct <= 0 || !row?.opened_at) return null;
  const openedMs = new Date(row.opened_at).getTime();
  if (!Number.isFinite(openedMs)) return null;
  const remainingHours = limitHours - (Date.now() - openedMs) / 3600000;
  if (remainingHours / limitHours > thresholdPct / 100) return null;
  if (remainingHours <= 0) return "Zorunlu kapanış bekleniyor";
  const h = Math.floor(remainingHours);
  const m = Math.max(Math.round((remainingHours - h) * 60), 0);
  return `${h > 0 ? `${h}s ` : ""}${m}dk sonra zorunlu kapanış`;
};

export const UserPositionsPage = () => {
  const navigate = useNavigate();
  const [openRows, setOpenRows] = useState([]);
  const [recentClosedRows, setRecentClosedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [partialSize, setPartialSize] = useState({});
  const [reverseSize, setReverseSize] = useState({});
  const [stopPrice, setStopPrice] = useState({});
  const [takeProfitPrice, setTakeProfitPrice] = useState({});

  const load = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.get("/user/execution/positions", { params: { include_closed: true } });
      const normalized = Array.isArray(data) ? data.map((row, index) => ({
        ...row,
        position_id: row?.position_id || row?.trade_id || `${row?.symbol || 'position'}-${index}`,
        size: row?.size ?? row?.quantity ?? 0,
      })) : [];
      setOpenRows(normalized.filter((row) => String(row?.status || "").toLowerCase() === "open"));
      setRecentClosedRows(normalized.filter((row) => String(row?.status || "").toLowerCase() !== "open").slice(0, 12));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pozisyonlar yüklenemedi");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      // 2026-07-08: `document.hidden` kontrolü KALDIRILDI — arka planda sekme
      // varken de liste güncellenir. Aksi halde drift-reconcile pozisyonları
      // arka planda kapatınca stale position_id ile 404 alınıyordu.
      load();
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  const runPositionAction = async (row, intentType, overrides = {}) => {
    const key = `${intentType}-${row.position_id}`;
    setBusyAction(key);
    try {
      // 2026-07-08: Close/Reverse'ten HEMEN önce listeyi yenile — position_id
      // hâlâ geçerli mi doğrula. Sekme uzun süre arka planda kaldıysa liste stale
      // olabilir; refresh sonrası eğer bu row artık listede yoksa erken uyarı verilir.
      const refreshed = await apiClient.get("/user/execution/positions").catch(() => null);
      if (refreshed?.data?.positions) {
        const stillPresent = refreshed.data.positions.some(
          (p) => String(p.trade_id || p.position_id) === String(row.position_id)
        );
        if (!stillPresent) {
          toast.info("Bu pozisyon güncellenmiş olabilir, liste yenileniyor...");
          await load();
          return;
        }
      }
      const payload = {
        intent_type: intentType,
        position_id: row.position_id,
        symbol: row.symbol,
        size: Number(overrides.size ?? row.size),
        reduce_only: overrides.reduce_only ?? true,
        price: overrides.price ?? null,
        stop_price: overrides.stop_price ?? null,
        take_profit_price: overrides.take_profit_price ?? null,
      };
      const preview = await apiClient.post("/user/execution/position-actions/preview", payload);
      if (preview.data?.validation_status !== "valid") {
        toast.error((preview.data?.reject_reason_codes || []).join(", ") || "Action preview başarısız");
        return;
      }

      await apiClient.post("/user/execution/position-actions/submit", {
        intent_token: preview.data.intent_token,
        preview_hash: preview.data.preview_hash,
      });
      toast.success(`${intentType} intent kuyruğa alındı`);
      await load();
    } catch (error) {
      // 2026-07-08: 404 durumunda daha anlamlı mesaj + otomatik refresh
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 404) {
        const diagReason = typeof detail === "object" ? detail?.diag_reason : null;
        const msg = diagReason
          ? `Bu pozisyon güncellenmiş (${diagReason}), liste yenileniyor...`
          : "Bu pozisyon güncellenmiş olabilir, liste yenileniyor...";
        toast.info(msg);
        await load();
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.reason_code || `${intentType} işlemi başarısız`);
        toast.error(msg);
      }
    } finally {
      setBusyAction("");
    }
  };

  if (isLoading) {
    return <LoadingSkeleton rows={8} testId="user-positions-loading-skeleton" />;
  }

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="user-positions-page">
      <header className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-positions-header">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-positions-title">Positions</h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="user-positions-description">Open pozisyon yönetimi: close, partial close, reverse, stop/tp güncelleme.</p>
      </header>

      <section className="col-span-12 grid gap-3 md:grid-cols-3" data-testid="user-positions-kpi-grid">
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="user-positions-kpi-open-count">
          <p className="text-xs text-slate-400">Open Positions</p>
          <p className="mt-2 text-2xl font-black text-emerald-200">{openRows.length}</p>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="user-positions-kpi-closed-count">
          <p className="text-xs text-slate-400">Recent Closed</p>
          <p className="mt-2 text-2xl font-black text-amber-200">{recentClosedRows.length}</p>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="user-positions-kpi-empty-reason">
          <p className="text-xs text-slate-400">View Mode</p>
          <p className="mt-2 text-sm text-slate-200">Positions ekranı artık açık pozisyon yoksa son kapananları da gösterir.</p>
        </article>
      </section>

      {openRows.length === 0 && (
        <section className="col-span-12 border border-amber-500/40 bg-amber-500/10 p-4" data-testid="user-positions-empty-state-panel">
          <p className="text-sm text-amber-100" data-testid="user-positions-empty-state-text">Şu anda açık pozisyon yok. Backend çağrısı boş dönüyor çünkü sistemde active/open pozisyon kalmamış; aşağıda son kapanan pozisyonları gösteriyorum.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="user-positions-empty-state-actions">
            <Button variant="outline" onClick={() => navigate("/user/scanner")} data-testid="user-positions-empty-go-scanner-button">Scanner’a Git</Button>
            <Button variant="outline" onClick={() => navigate("/user/signals")} data-testid="user-positions-empty-go-signals-button">Signals’a Git</Button>
            <Button variant="outline" onClick={() => navigate("/user/signals")} data-testid="user-positions-empty-go-trade-button">Signals’a Git</Button>
          </div>
        </section>
      )}

      <div className="col-span-12 overflow-x-auto border border-slate-800 bg-slate-900" data-testid="user-positions-table-wrapper">
        <table className="min-w-full text-sm" data-testid="user-positions-table">
          <thead className="bg-slate-800 text-left" data-testid="user-positions-table-head">
            <tr>
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Side</th>
              <th className="px-3 py-2">Size</th>
              <th className="px-3 py-2">Entry</th>
              <th className="px-3 py-2">Current</th>
              <th className="px-3 py-2">TP / SL</th>
              <th className="px-3 py-2">Unrealized PnL</th>
              <th className="px-3 py-2">Leverage</th>
              <th className="px-3 py-2">Live Status</th>
              <th className="px-3 py-2">Exit State</th>
              <th className="px-3 py-2">Strategy</th>
              <th className="px-3 py-2">Cluster</th>
              <th className="px-3 py-2">Recommended Action</th>
              <th className="px-3 py-2">Risk Reduction Score</th>
              <th className="px-3 py-2">Hedge Suggestion</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="user-positions-table-body">
            {openRows.map((row, index) => {
              const rowId = String(row.position_id || row.trade_id || `${row.symbol || 'position'}-${index}`);
              const baseSize = Math.max(toSafeNumber(row.size, 0), 0.001);
              const defaultPartialSize = Math.max(baseSize / 2, 0.001);
              const exitState = row.exit_state || null;
              const needsRecovery = exitState === "EXIT_RECOVERY_REQUIRED" || exitState === "EXIT_MANUAL_REVIEW";
              return (
              <tr key={rowId} className="border-t border-slate-800" data-testid={`user-positions-row-${rowId}`}>
                <td className="px-3 py-2" data-testid={`user-positions-symbol-${rowId}`}>
                  {row.symbol}
                  {maxHoldingCountdown(row) && (
                    <span
                      className="mt-1 block w-fit rounded-full border border-amber-500/60 bg-amber-500/15 px-2 py-0.5 text-xs text-amber-200"
                      data-testid={`user-positions-max-holding-badge-${rowId}`}
                    >
                      ⏳ {maxHoldingCountdown(row)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 uppercase" data-testid={`user-positions-side-${rowId}`}>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${String(row.side).toLowerCase() === "sell" || String(row.side).toLowerCase() === "short" ? "border-rose-500/60 bg-rose-500/15 text-rose-200" : "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"}`}>
                    {row.side || "-"}
                  </span>
                </td>
                <td className="px-3 py-2" data-testid={`user-positions-size-${rowId}`}>{row.size}</td>
                <td className="px-3 py-2" data-testid={`user-positions-entry-${rowId}`}>{row.entry_price}</td>
                <td className="px-3 py-2" data-testid={`user-positions-current-${rowId}`}>{row.current_price}</td>
                <td className="px-3 py-2 text-xs" data-testid={`user-positions-tp-sl-${rowId}`}>
                  <div>TP: {row.take_profit_price ?? "-"}</div>
                  <div>SL: {row.stop_price ?? "-"}</div>
                </td>
                <td className="px-3 py-2" data-testid={`user-positions-unrealized-${rowId}`}>{row.unrealized_pnl}</td>
                <td className="px-3 py-2" data-testid={`user-positions-leverage-${rowId}`}>{row.leverage}</td>
                <td className="px-3 py-2" data-testid={`user-positions-execution-mode-${rowId}`}>
                  <span className="inline-flex rounded-full border border-emerald-500/60 bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-100" data-testid={`user-positions-execution-mode-badge-${rowId}`}>
                    live
                  </span>
                </td>
                <td className="px-3 py-2 text-xs" data-testid={`user-positions-exit-state-${rowId}`}>
                  {exitState ? (
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 ${needsRecovery ? "border-red-500 bg-red-500/20 text-red-200 font-semibold" : "border-slate-600 bg-slate-800 text-slate-300"}`}
                      data-testid={`user-positions-exit-state-badge-${rowId}`}
                      title={row.last_exit_error_code ? `Kod: ${row.last_exit_error_code}` : undefined}
                    >
                      {needsRecovery ? "⚠ Exit Hatası: Manuel İnceleme Gerekli" : exitState}
                    </span>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                  {row.exit_retry_count > 0 && (
                    <div className="mt-1 text-slate-500" data-testid={`user-positions-exit-retry-count-${rowId}`}>
                      Retry: {row.exit_retry_count}
                      {row.next_exit_attempt_at ? ` · sonraki: ${new Date(row.next_exit_attempt_at).toLocaleTimeString()}` : ""}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2" data-testid={`user-positions-strategy-${rowId}`}>{row.strategy_id || "-"}</td>
                <td className="px-3 py-2" data-testid={`user-positions-cluster-${rowId}`}>{row.cluster_id || "UNCLUSTERED"}</td>
                <td className="px-3 py-2" data-testid={`user-positions-recommended-action-${rowId}`}>{row.recommended_action || "monitor"}</td>
                <td className="px-3 py-2" data-testid={`user-positions-risk-reduction-score-${rowId}`}>{row.risk_reduction_score ?? 0}</td>
                <td className="px-3 py-2" data-testid={`user-positions-hedge-suggestion-${rowId}`}>{row.hedge_suggestion?.hedge_symbol ? `${row.hedge_suggestion.hedge_symbol} ${row.hedge_suggestion.hedge_direction} ${row.hedge_suggestion.hedge_size}` : "none"}</td>
                <td className="px-3 py-2">
                  <div className="grid gap-2" data-testid={`user-positions-actions-${rowId}`}>
                    <p className="text-xs text-slate-400" data-testid={`user-positions-action-explainability-${rowId}`}>
                      Why: recommended_action={row.recommended_action || "monitor"} / risk_reduction_score={row.risk_reduction_score ?? 0}
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid={`user-positions-primary-actions-${rowId}`}>
                      <Button
                        className="bg-rose-500 text-white hover:bg-rose-400"
                        disabled={busyAction === `CLOSE_POSITION-${rowId}`}
                        onClick={() => runPositionAction(row, "CLOSE_POSITION", { size: row.size, reduce_only: true })}
                        data-testid={`user-positions-close-button-${rowId}`}
                      >
                        Close
                      </Button>

                      <Input
                        className="w-24"
                        type="number"
                        value={partialSize[rowId] ?? defaultPartialSize}
                        onChange={(event) => setPartialSize((prev) => ({ ...prev, [rowId]: event.target.value }))}
                        data-testid={`user-positions-partial-size-input-${rowId}`}
                      />
                      <Button
                        variant="outline"
                        disabled={busyAction === `PARTIAL_CLOSE-${rowId}`}
                        onClick={() =>
                          runPositionAction(row, "PARTIAL_CLOSE", {
                            size: toSafeNumber(partialSize[rowId] ?? defaultPartialSize, defaultPartialSize),
                            reduce_only: true,
                          })
                        }
                        data-testid={`user-positions-partial-close-button-${rowId}`}
                      >
                        Partial Close
                      </Button>

                      <Input
                        className="w-24"
                        type="number"
                        value={reverseSize[rowId] ?? baseSize}
                        onChange={(event) => setReverseSize((prev) => ({ ...prev, [rowId]: event.target.value }))}
                        data-testid={`user-positions-reverse-size-input-${rowId}`}
                      />
                      <Button
                        variant="outline"
                        disabled={busyAction === `REVERSE_POSITION-${rowId}`}
                        onClick={() =>
                          runPositionAction(row, "REVERSE_POSITION", {
                            size: toSafeNumber(reverseSize[rowId] ?? baseSize, baseSize),
                            reduce_only: false,
                          })
                        }
                        data-testid={`user-positions-reverse-button-${rowId}`}
                      >
                        Reverse
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2" data-testid={`user-positions-risk-actions-${rowId}`}>
                      <div className="flex flex-col gap-1" data-testid={`user-positions-stop-group-${rowId}`}>
                        <label className="text-[11px] text-slate-400" htmlFor={`user-positions-stop-input-${rowId}`} data-testid={`user-positions-stop-label-${rowId}`}>Stop Price</label>
                        <Input
                          id={`user-positions-stop-input-${rowId}`}
                          className="w-28"
                          type="number"
                          value={stopPrice[rowId] ?? ""}
                          placeholder="stop"
                          onChange={(event) => setStopPrice((prev) => ({ ...prev, [rowId]: event.target.value }))}
                          data-testid={`user-positions-stop-input-${rowId}`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        disabled={busyAction === `MOVE_STOP-${rowId}`}
                        onClick={() =>
                          runPositionAction(row, "MOVE_STOP", {
                            size: baseSize,
                            stop_price: toSafeNumber(stopPrice[rowId], 0),
                            reduce_only: true,
                          })
                        }
                        data-testid={`user-positions-move-stop-button-${rowId}`}
                      >
                        Edit Stop
                      </Button>

                      <div className="flex flex-col gap-1" data-testid={`user-positions-tp-group-${rowId}`}>
                        <label className="text-[11px] text-slate-400" htmlFor={`user-positions-tp-input-${rowId}`} data-testid={`user-positions-tp-label-${rowId}`}>Take Profit Price</label>
                        <Input
                          id={`user-positions-tp-input-${rowId}`}
                          className="w-28"
                          type="number"
                          value={takeProfitPrice[rowId] ?? ""}
                          placeholder="take profit"
                          onChange={(event) => setTakeProfitPrice((prev) => ({ ...prev, [rowId]: event.target.value }))}
                          data-testid={`user-positions-tp-input-${rowId}`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        disabled={busyAction === `MOVE_TAKE_PROFIT-${rowId}`}
                        onClick={() =>
                          runPositionAction(row, "MOVE_TAKE_PROFIT", {
                            size: baseSize,
                            take_profit_price: toSafeNumber(takeProfitPrice[rowId], 0),
                            reduce_only: true,
                          })
                        }
                        data-testid={`user-positions-move-tp-button-${rowId}`}
                      >
                        Edit Take Profit
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {recentClosedRows.length > 0 && (
        <section className="col-span-12 border border-slate-800 bg-slate-900" data-testid="user-positions-recent-closed-panel">
          <div className="border-b border-slate-800 px-4 py-3" data-testid="user-positions-recent-closed-header">
            <h3 className="text-lg font-black text-slate-100" data-testid="user-positions-recent-closed-title">Son Kapanan Pozisyonlar</h3>
            <p className="mt-1 text-xs text-slate-400" data-testid="user-positions-recent-closed-description">Açık pozisyon yoksa ekranın boş görünmemesi için son 12 kapanan işlemi gösteriyorum.</p>
          </div>
          <div className="overflow-x-auto" data-testid="user-positions-recent-closed-table-wrapper">
            <table className="min-w-full text-sm" data-testid="user-positions-recent-closed-table">
              <thead className="bg-slate-800/70 text-left">
                <tr>
                  <th className="px-3 py-2">Symbol</th>
                  <th className="px-3 py-2">Side</th>
                  <th className="px-3 py-2">Entry</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Exit State</th>
                  <th className="px-3 py-2">Exit Reason</th>
                  <th className="px-3 py-2">Closed At</th>
                </tr>
              </thead>
              <tbody data-testid="user-positions-recent-closed-table-body">
                {recentClosedRows.map((row, index) => {
                  const rowId = String(row.position_id || row.trade_id || `${row.symbol || 'closed'}-${index}`);
                  return (
                    <tr key={rowId} className="border-t border-slate-800" data-testid={`user-positions-recent-closed-row-${rowId}`}>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-symbol-${rowId}`}>{row.symbol}</td>
                      <td className="px-3 py-2 uppercase" data-testid={`user-positions-recent-closed-side-${rowId}`}>
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${String(row.side).toLowerCase() === "sell" || String(row.side).toLowerCase() === "short" ? "border-rose-500/60 bg-rose-500/15 text-rose-200" : "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"}`}>
                          {row.side || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-entry-${rowId}`}>{row.entry_price ?? '-'}</td>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-status-${rowId}`}>{row.status}</td>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-exit-state-${rowId}`}>{row.exit_state || '-'}</td>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-exit-reason-${rowId}`}>{row.exit_reason || row.last_exit_error || '-'}</td>
                      <td className="px-3 py-2" data-testid={`user-positions-recent-closed-closed-at-${rowId}`}>{row.closed_at || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  );
};
